export interface AgentTransport {
    request(data: object): Promise<any>;
}

interface RpcCallStep {
    method: string;
    args: any[];
    kwargs: Record<string, any>;
}

/** Sends an `rpc_call` message (see `agent.api.handlers.rpc.handle_rpc_call`)
 * that walks from the root Session through `chainPath` -- each entry a plain
 * field-navigation step -- then calls `method(*args, **kwargs)` on whatever
 * that chain reaches. `chainPath` is the generated Store's own path from the
 * session root (see `*.generated.ts`'s constructor). */
export function callRpc(
    transport: AgentTransport,
    chainPath: string[],
    method: string,
    args: any[] = [],
    kwargs: Record<string, any> = {},
): Promise<any> {
    // Drop trailing omitted (undefined) positional args. A JS array serializes
    // `undefined` to JSON `null`, so an omitted optional arg would otherwise
    // reach Python as an explicit `None` and defeat the parameter's default
    // (e.g. `pull(remote="origin")` invoked as `pull(None)`). Omitted *keyword*
    // args need no such handling -- JSON.stringify drops undefined-valued keys.
    // Only `undefined` is trimmed; an explicit `null` is a deliberate `None`.
    const trimmed = [...args];
    while (trimmed.length && trimmed[trimmed.length - 1] === undefined) {
        trimmed.pop();
    }
    const chain: RpcCallStep[] = [
        ...chainPath.map(step => ({ method: step, args: [], kwargs: {} })),
        { method, args: trimmed, kwargs },
    ];
    return transport.request({ type: "rpc_call", chain });
}
